// The club's athlete-access panel (see `features/athlete-access.md`), shown on an
// athlete's profile page. It answers "who can sign in to this profile?" and lets
// staff invite the athlete or their parent/carer, take back an unclaimed
// invitation, and withdraw a grant.
//
// Reading needs company access, like the rest of the profile page; every mutation
// needs MANAGE_ATHLETE_ACCESS — a separate permission from MANAGE_ATHLETES,
// because handing out platform access is a different act from correcting a date
// of birth. The server enforces both on every request; this only gates what is
// rendered.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import type {
  AthleteAccessPanel,
  AthleteAccessRelation,
  AthleteAccessView,
  AthleteInviteView,
} from '../domain';
import { ATHLETE_ACCESS_RELATIONS, isValidEmail } from '../domain';
import {
  cancelAthleteInvite,
  fetchAthleteAccess,
  inviteAthleteAccess,
  revokeAthleteAccess,
} from '../data/apiClient';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from '../state/I18nContext';

interface AthleteAccessSectionProps {
  personalNumber: string;
  athleteName: string;
  /** Whether the athlete is under 18 — decides which relation we suggest first. */
  isMinor: boolean;
}

/** A grant or a pending invitation, unified for the list. */
type Row =
  | { kind: 'grant'; grant: AthleteAccessView }
  | { kind: 'invite'; invite: AthleteInviteView };

export function AthleteAccessSection({
  personalNumber,
  athleteName,
  isMinor,
}: AthleteAccessSectionProps) {
  const { hasPermission } = useAuth();
  const { t, localeCode } = useTranslation();
  const canManage = hasPermission('MANAGE_ATHLETE_ACCESS');

  const [panel, setPanel] = useState<AthleteAccessPanel | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState('');
  // A minor's access goes to a guardian by default; an adult claims their own.
  const [relation, setRelation] = useState<AthleteAccessRelation>(
    isMinor ? 'GUARDIAN' : 'SELF',
  );
  const [emailError, setEmailError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<AthleteAccessView | null>(null);

  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPanel(await fetchAthleteAccess(personalNumber));
      setError(null);
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : tRef.current('admin.athleteAccess.loadFailed'),
      );
    } finally {
      setLoading(false);
    }
  }, [personalNumber]);

  useEffect(() => {
    void load();
  }, [load]);

  const dateFormat = useMemo(
    () => new Intl.DateTimeFormat(localeCode, { dateStyle: 'medium' }),
    [localeCode],
  );

  const rows = useMemo((): Row[] => {
    if (!panel) return [];
    return [
      ...panel.grants.map((grant): Row => ({ kind: 'grant', grant })),
      ...panel.pending.map((invite): Row => ({ kind: 'invite', invite })),
    ];
  }, [panel]);

  const submitInvite = async () => {
    if (!isValidEmail(email)) {
      setEmailError(t('admin.athleteAccess.invite.emailInvalid'));
      return;
    }
    setBusy(true);
    setError(null);
    setEmailError(null);
    try {
      await inviteAthleteAccess(personalNumber, email.trim(), relation);
      setInviteOpen(false);
      setEmail('');
      setNotice(t('admin.athleteAccess.invite.sent', { email: email.trim() }));
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.athleteAccess.invite.failed'));
    } finally {
      setBusy(false);
    }
  };

  const confirmRevoke = async () => {
    if (!revoking) return;
    setBusy(true);
    setError(null);
    try {
      setPanel(await revokeAthleteAccess(personalNumber, revoking.id));
      setRevoking(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.athleteAccess.revoke.failed'));
    } finally {
      setBusy(false);
    }
  };

  const cancelInvite = async (invite: AthleteInviteView) => {
    setBusy(true);
    setError(null);
    try {
      setPanel(await cancelAthleteInvite(personalNumber, invite.email, invite.relation));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.athleteAccess.invite.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {t('admin.athleteAccess.title')}
        </Typography>
        {canManage && (
          <Button
            size="small"
            startIcon={<PersonAddIcon />}
            disabled={busy}
            onClick={() => {
              setRelation(isMinor ? 'GUARDIAN' : 'SELF');
              setEmailError(null);
              setInviteOpen(true);
            }}
          >
            {t('admin.athleteAccess.inviteAction')}
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(isMinor ? 'admin.athleteAccess.hintMinor' : 'admin.athleteAccess.hintAdult')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('admin.athleteAccess.empty')}
        </Typography>
      ) : (
        <Stack divider={<Divider flexItem />} spacing={0}>
          {rows.map((row) =>
            row.kind === 'grant' ? (
              <Stack
                key={row.grant.id}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ py: 1.5, opacity: row.grant.revokedAt === null ? 1 : 0.55 }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap title={row.grant.email}>
                    {row.grant.email}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    <Chip
                      size="small"
                      color={row.grant.relation === 'GUARDIAN' ? 'secondary' : 'primary'}
                      variant="outlined"
                      label={t(`admin.athleteAccess.relation.${row.grant.relation}`)}
                    />
                    {row.grant.revokedAt === null ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t('admin.athleteAccess.since', {
                          date: dateFormat.format(new Date(row.grant.createdAt)),
                        })}
                      />
                    ) : (
                      <Chip
                        size="small"
                        color="default"
                        label={t('admin.athleteAccess.revokedOn', {
                          date: dateFormat.format(new Date(row.grant.revokedAt)),
                        })}
                      />
                    )}
                  </Stack>
                </Box>
                {canManage && row.grant.revokedAt === null && (
                  <Tooltip title={t('admin.athleteAccess.revoke.action')}>
                    <span>
                      <IconButton
                        aria-label={t('admin.athleteAccess.revoke.aria', {
                          email: row.grant.email,
                        })}
                        disabled={busy}
                        onClick={() => setRevoking(row.grant)}
                      >
                        <LinkOffIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            ) : (
              <Stack
                key={row.invite.id}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ py: 1.5 }}
              >
                <MailOutlineIcon color="disabled" fontSize="small" />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap title={row.invite.email}>
                    {row.invite.email}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t(`admin.athleteAccess.relation.${row.invite.relation}`)}
                    />
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={t('admin.athleteAccess.pendingUntil', {
                        date: dateFormat.format(new Date(row.invite.expiresAt)),
                      })}
                    />
                  </Stack>
                </Box>
                {canManage && (
                  <Tooltip title={t('admin.athleteAccess.invite.cancel')}>
                    <span>
                      <IconButton
                        aria-label={t('admin.athleteAccess.invite.cancelAria', {
                          email: row.invite.email,
                        })}
                        disabled={busy}
                        onClick={() => void cancelInvite(row.invite)}
                      >
                        <LinkOffIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            ),
          )}
        </Stack>
      )}

      {/* Invite dialog. */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('admin.athleteAccess.invite.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('admin.athleteAccess.invite.body', { name: athleteName })}
          </DialogContentText>
          <Stack spacing={2}>
            <TextField
              select
              fullWidth
              label={t('admin.athleteAccess.invite.relation')}
              value={relation}
              onChange={(e) => setRelation(e.target.value as AthleteAccessRelation)}
              helperText={t(`admin.athleteAccess.relationHelp.${relation}`)}
            >
              {ATHLETE_ACCESS_RELATIONS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`admin.athleteAccess.relation.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              autoFocus
              type="email"
              label={t('admin.athleteAccess.invite.email')}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError(null);
              }}
              error={emailError !== null}
              helperText={emailError ?? t('admin.athleteAccess.invite.emailHelp')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)} disabled={busy}>
            {t('admin.athleteAccess.invite.cancelButton')}
          </Button>
          <Button variant="contained" onClick={() => void submitInvite()} disabled={busy}>
            {t('admin.athleteAccess.invite.send')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirmation. */}
      <Dialog open={revoking !== null} onClose={() => setRevoking(null)}>
        <DialogTitle>{t('admin.athleteAccess.revoke.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('admin.athleteAccess.revoke.body', {
              email: revoking?.email ?? '',
              name: athleteName,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevoking(null)} disabled={busy}>
            {t('admin.athleteAccess.invite.cancelButton')}
          </Button>
          <Button color="error" variant="contained" onClick={() => void confirmRevoke()} disabled={busy}>
            {t('admin.athleteAccess.revoke.action')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
